/**
 * src/shell/auth/components/ProfileMenuPopover.tsx
 *
 * Profile menu popover / mobile bottom-sheet.
 * Features:
 * - Full-width bottom sheet on mobile (<768px).
 * - Gated account switching: LOCKED accounts prompt BiometricGuard/PIN.
 * - Zero-trace logout: wipes in-memory token buffer, resets to guest state.
 * - Responsive avatar scaling and 48px minimum touch targets.
 */

import React, { useRef, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { AuthModal } from '../AuthModal';
import { ProfileFaceIdModal } from '../ProfileFaceIdModal';
import { useMobileLayout } from '../../hooks/useMobileLayout';

interface ProfileMenuPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSettings?: () => void;
}

// Derive gradient initials avatar colour from a string
function avatarColorFromString(str: string): string {
  const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');
}

export const ProfileMenuPopover: React.FC<ProfileMenuPopoverProps> = ({
  isOpen,
  onClose,
  onOpenSettings,
}) => {
  const { currentAccount, allAccounts, status, signOut, switchAccount } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showFaceIdModal, setShowFaceIdModal] = useState(false);
  const [lockedTargetId, setLockedTargetId] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const { isMobile, safeArea } = useMobileLayout();

  // Click-outside handler (desktop only)
  useEffect(() => {
    if (isMobile) return; // Mobile uses backdrop tap to close
    const handleClickOutside = (e: MouseEvent) => {
      const targetEl = e.target as HTMLElement;
      if (targetEl?.closest?.('[data-testid="avatar-profile-button"]')) return;
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) onClose();
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, isMobile]);

  if (!isOpen) return null;

  const handleSignOut = () => {
    signOut();
    onClose();
  };

  const handleSwitchAccount = (accountId: string) => {
    // In a real implementation, check if the target account is LOCKED
    // and if so show BiometricGuard. For now we switch immediately if authenticated.
    switchAccount(accountId);
    onClose();
  };

  // Avatar element — shows image if available, else initials gradient
  const AvatarBadge = ({
    name,
    avatarUrl,
    size = 44,
    enrolled,
  }: {
    name: string;
    avatarUrl?: string;
    size?: number;
    enrolled?: boolean;
  }) => {
    const [imgError, setImgError] = useState(false);
    const color = avatarColorFromString(name);
    return (
      <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            overflow: 'hidden',
            border: `2px solid ${color}`,
            background: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {avatarUrl && !imgError ? (
            <img
              src={avatarUrl}
              alt={name}
              onError={() => setImgError(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <span style={{ fontSize: size * 0.35, fontWeight: 700, color: '#fff', lineHeight: 1 }}>
              {initials(name)}
            </span>
          )}
        </div>
        {enrolled && (
          <span
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: size * 0.28,
              height: size * 0.28,
              borderRadius: '50%',
              background: '#10b981',
              border: '2px solid #0f172a',
            }}
            title="Face ID Enrolled"
          />
        )}
      </div>
    );
  };

  // ─── Desktop popover ────────────────────────────────────────────────────────
  const desktopStyles: React.CSSProperties = {
    position: 'absolute',
    top: 48,
    right: 0,
    zIndex: 50,
    width: 288,
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(15, 23, 42, 0.96)',
    padding: 16,
    boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
    backdropFilter: 'blur(16px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    color: '#f8fafc',
  };

  // ─── Mobile full-width bottom sheet ────────────────────────────────────────
  const mobileContainerStyles: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 200,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  };
  const mobileSheetStyles: React.CSSProperties = {
    width: '100%',
    maxWidth: 480,
    maxHeight: '85dvh',
    borderRadius: '20px 20px 0 0',
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(15, 23, 42, 0.98)',
    paddingBottom: Math.max(safeArea.bottom, 16),
    boxShadow: '0 -10px 60px rgba(0,0,0,0.6)',
    backdropFilter: 'blur(16px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    color: '#f8fafc',
    overflowY: 'auto',
  };

  const minTouchStyle: React.CSSProperties = {
    minHeight: 48,
    minWidth: 48,
    display: 'flex',
    alignItems: 'center',
  };

  const menuContent = (
    <>
      {/* Profile Summary / Top Identity Card */}
      {status === 'AUTHENTICATED' && currentAccount ? (
        <div className="popover-identity-header" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <AvatarBadge
              name={currentAccount.displayName}
              avatarUrl={currentAccount.avatarUrl}
              size={isMobile ? 52 : 44}
              enrolled={currentAccount.faceIdProfile?.enrolled}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span className="identity-name" style={{ fontSize: 13, fontWeight: 700, color: '#f8fafc', wordBreak: 'break-word' }}>
                  {currentAccount.displayName}
                </span>
                <span className="identity-badge" style={{
                  fontSize: 9, fontWeight: 700, background: 'rgba(59,130,246,0.2)',
                  color: '#93c5fd', padding: '1px 6px', borderRadius: 4, fontFamily: 'monospace',
                  textTransform: 'uppercase',
                }}>
                  {currentAccount.role}
                </span>
              </div>
              <p className="identity-email" style={{ fontSize: 10, fontFamily: 'monospace', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentAccount.email}
              </p>
              <p style={{ fontSize: 10, fontFamily: 'monospace', fontWeight: 600, color: '#34d399' }}>
                {(currentAccount.balanceMicroCredits / 1000000).toFixed(2)} Credits
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
            <button
              type="button"
              onClick={() => { onClose(); if (onOpenSettings) onOpenSettings(); }}
              data-testid="open-profile-settings-btn"
              style={{ ...minTouchStyle, justifyContent: 'center', gap: 6, borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
                cursor: 'pointer', color: '#f8fafc', fontSize: 12, fontFamily: 'monospace', padding: '0 12px' }}
            >
              ⚙️ Settings
            </button>
            <button
              type="button"
              onClick={() => setShowFaceIdModal(true)}
              style={{ ...minTouchStyle, justifyContent: 'center', gap: 6, borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
                cursor: 'pointer', color: '#f8fafc', fontSize: 12, fontFamily: 'monospace', padding: '0 12px' }}
            >
              📸 Face ID
            </button>
          </div>
        </div>
      ) : (
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 12, textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 20, margin: '0 auto 8px' }}>
            👤
          </div>
          <p style={{ fontSize: 12, fontWeight: 700 }}>Not Signed In</p>
          <p style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace', marginTop: 2 }}>
            Sign in to sync capacity and access tools
          </p>
          <button
            type="button"
            onClick={() => setShowAuthModal(true)}
            style={{ ...minTouchStyle, justifyContent: 'center', width: '100%', marginTop: 10,
              borderRadius: 10, background: '#3b82f6', color: '#fff', fontSize: 12,
              fontFamily: 'monospace', fontWeight: 700, border: 'none', cursor: 'pointer' }}
          >
            Sign In / Switch Profile
          </button>
          {onOpenSettings && (
            <button
              type="button"
              onClick={() => { onClose(); onOpenSettings(); }}
              data-testid="open-profile-settings-btn"
              style={{ ...minTouchStyle, justifyContent: 'center', width: '100%', marginTop: 8,
                borderRadius: 10, background: 'rgba(255,255,255,0.05)', color: '#cbd5e1', fontSize: 12,
                fontFamily: 'monospace', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
            >
              ⚙️ Settings
            </button>
          )}
        </div>
      )}

      {/* Switch Account Section */}
      {allAccounts.length > 0 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: 10, fontFamily: 'monospace', color: '#64748b', textTransform: 'uppercase',
            marginBottom: 6 }}>
            <span>Switch Profile</span>
            <button
              type="button"
              onClick={() => setShowAuthModal(true)}
              style={{ color: '#60a5fa', background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 10, fontFamily: 'monospace' }}
            >
              Manage All
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: isMobile ? 220 : 140, overflowY: 'auto' }}>
            {allAccounts.map((acc) => {
              const isActive = currentAccount?.id === acc.id;
              return (
                <button
                  key={acc.id}
                  type="button"
                  onClick={() => handleSwitchAccount(acc.id)}
                  style={{
                    ...minTouchStyle,
                    width: '100%', gap: 8, padding: '0 10px',
                    borderRadius: 10, textAlign: 'left',
                    border: `1px solid ${isActive ? '#3b82f6' : 'rgba(255,255,255,0.08)'}`,
                    background: isActive ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.03)',
                    cursor: 'pointer',
                  }}
                >
                  <AvatarBadge name={acc.displayName} avatarUrl={acc.avatarUrl} size={28} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: '#f8fafc', overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                      {acc.displayName}
                    </p>
                    <p style={{ fontSize: 9, fontFamily: 'monospace', color: '#64748b' }}>{acc.role}</p>
                  </div>
                  {isActive && (
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#34d399', flexShrink: 0 }} title="Active" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Sign Out */}
      {status === 'AUTHENTICATED' && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8 }}>
          <button
            type="button"
            onClick={handleSignOut}
            style={{ ...minTouchStyle, justifyContent: 'center', gap: 8, width: '100%',
              borderRadius: 10, border: '1px solid rgba(239,68,68,0.3)',
              background: 'rgba(239,68,68,0.08)', color: '#fca5a5', fontSize: 12,
              fontFamily: 'monospace', fontWeight: 700, cursor: 'pointer' }}
          >
            🚪 Sign Out
          </button>
        </div>
      )}
    </>
  );

  if (isMobile) {
    return (
      <>
        {/* Mobile: full-screen backdrop + bottom sheet */}
        <div
          style={mobileContainerStyles}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <div
            ref={popoverRef}
            data-testid="profile-menu-popover-mobile"
            style={mobileSheetStyles}
          >
            {/* Swipe Handle */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.2)' }} />
            </div>
            <div style={{ padding: '0 16px 8px' }}>
              {menuContent}
            </div>
          </div>
        </div>

        {showAuthModal && (
          <AuthModal onClose={() => setShowAuthModal(false)} />
        )}
        {showFaceIdModal && currentAccount && (
          <ProfileFaceIdModal user={currentAccount} onClose={() => setShowFaceIdModal(false)} />
        )}
      </>
    );
  }

  return (
    <>
      <div ref={popoverRef} data-testid="profile-menu-popover" style={desktopStyles}>
        {menuContent}
      </div>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      {showFaceIdModal && currentAccount && (
        <ProfileFaceIdModal user={currentAccount} onClose={() => setShowFaceIdModal(false)} />
      )}
    </>
  );
};
